import { useMemo, useState } from 'react';
import { format, eachDayOfInterval, addDays, differenceInCalendarDays, parseISO, isWeekend, startOfDay } from 'date-fns';
import type { ScheduleTask } from '../hooks/useSchedule';
import type { ScheduleSlot } from '../hooks/useScheduleSlots';
import { laborBucketConfig, statusConfig } from '../hooks/useSchedule';

// --- Compressed hour range constants ---
export const DAY_START_HOUR = 6;
export const DAY_END_HOUR = 18;
export const HOURS_PER_DAY = DAY_END_HOUR - DAY_START_HOUR; // 12

// --- Availability shading types ---
export interface PersonDayAvailability {
  type: 'working' | 'pto' | 'holiday' | 'sick' | 'off';
  name?: string; // holiday name
  slots: { start: string; end: string }[];
}

export interface AvailabilityData {
  [personId: string]: {
    name: string;
    lunch?: { start: string; durationMinutes: number } | null;
    days: { [date: string]: PersonDayAvailability };
  };
}

interface GanttChartProps {
  tasks: ScheduleTask[];
  slots?: ScheduleSlot[];
  startDate: Date;
  endDate: Date;
  colorBy?: 'laborBucket' | 'status';
  availability?: AvailabilityData;
}

/**
 * Convert a timestamp to compressed minutes within the visible hour range.
 * Hours outside DAY_START_HOUR..DAY_END_HOUR are clamped to the edges.
 * Returns minutes relative to the start of the compressed grid.
 */
export function toCompressedMinutes(timestamp: Date, gridStart: Date): number {
  const dayStart = startOfDay(timestamp);
  const dayIndex = differenceInCalendarDays(dayStart, startOfDay(gridStart));

  const hourOfDay = timestamp.getHours() + timestamp.getMinutes() / 60;
  const clampedHour = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR, hourOfDay));

  return dayIndex * HOURS_PER_DAY * 60 + (clampedHour - DAY_START_HOUR) * 60;
}

// Calculate the position and width of a task bar relative to a compressed day-based grid.
// The grid uses HOURS_PER_DAY (6am-6pm) per day rather than 24 hours, so bars only
// span the visible working window.
export function calculateTaskPosition(
  task: ScheduleTask,
  gridStart: Date,
  _gridEnd: Date,
  totalDays: number
): { left: string; width: string } | null {
  if (!task.scheduledStart || !task.scheduledEnd) {
    return null;
  }

  const taskStart = parseISO(task.scheduledStart);
  const taskEnd = parseISO(task.scheduledEnd);

  const totalMinutes = totalDays * HOURS_PER_DAY * 60;
  const startMin = toCompressedMinutes(taskStart, gridStart);
  const endMin = toCompressedMinutes(taskEnd, gridStart);

  // Entirely outside visible range (same compressed position = hidden)
  if (endMin <= startMin) return null;

  const left = (startMin / totalMinutes) * 100;
  const width = ((endMin - startMin) / totalMinutes) * 100;

  return {
    left: `${Math.max(0, left)}%`,
    width: `${Math.min(100 - Math.max(0, left), Math.max(0.3, width))}%`,
  };
}

// Calculate position for a schedule slot using compressed coordinates (6am-6pm)
export function calculateSlotPosition(
  slot: ScheduleSlot,
  gridStart: Date,
  _gridEnd: Date,
  totalDays: number
): { left: string; width: string } | null {
  const slotStart = parseISO(slot.startTime);
  const slotEnd = parseISO(slot.endTime);

  const totalMinutes = totalDays * HOURS_PER_DAY * 60;
  const startMin = toCompressedMinutes(slotStart, gridStart);
  const endMin = toCompressedMinutes(slotEnd, gridStart);

  // Entirely outside visible range (same compressed position = hidden)
  if (endMin <= startMin) return null;

  const left = (startMin / totalMinutes) * 100;
  const width = ((endMin - startMin) / totalMinutes) * 100;

  return {
    left: `${Math.max(0, left)}%`,
    width: `${Math.min(100 - Math.max(0, left), Math.max(0.3, width))}%`,
  };
}

// Detect which taskIds have slots assigned to multiple people (split tasks)
function detectSplitTaskIds(slots: ScheduleSlot[]): Set<string> {
  const taskPersons = new Map<string, Set<string>>();
  for (const slot of slots) {
    const persons = taskPersons.get(slot.taskId) ?? new Set<string>();
    persons.add(slot.personId);
    taskPersons.set(slot.taskId, persons);
  }
  const splitIds = new Set<string>();
  for (const [taskId, persons] of taskPersons) {
    if (persons.size > 1) {
      splitIds.add(taskId);
    }
  }
  return splitIds;
}

// Format duration in minutes to a human-friendly string
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function GanttChart({ tasks, slots, startDate, endDate, colorBy = 'laborBucket', availability }: GanttChartProps) {
  const [hoveredTask, setHoveredTask] = useState<ScheduleTask | null>(null);
  const [hoveredSlot, setHoveredSlot] = useState<ScheduleSlot | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Normalize to start-of-day so the positioning grid aligns with the visual day columns.
  // The column headers use eachDayOfInterval which returns dates at midnight local,
  // so the positioning must also use midnight boundaries.
  const gridStart = useMemo(() => startOfDay(startDate), [startDate]);

  // Generate array of days for the header
  const days = useMemo(
    () => eachDayOfInterval({ start: gridStart, end: endDate }),
    [gridStart, endDate]
  );

  const totalDays = days.length;

  // End of the last visible day: gridStart + totalDays days
  const gridEnd = useMemo(() => addDays(gridStart, totalDays), [gridStart, totalDays]);

  // Group slots by personId (keyed by personId, display personName) to avoid
  // merging rows when two people share the same name.
  // Returns [personName, slots, personId] tuples for availability shading support.
  const slotsByPerson = useMemo(() => {
    if (!slots || slots.length === 0) return null;

    const grouped = new Map<string, { name: string; personId: string; slots: ScheduleSlot[] }>();
    slots.forEach((slot) => {
      const existing = grouped.get(slot.personId);
      if (existing) {
        existing.slots.push(slot);
      } else {
        grouped.set(slot.personId, { name: slot.personName, personId: slot.personId, slots: [slot] });
      }
    });

    return Array.from(grouped.values())
      .map(({ name, personId, slots: s }) => [name, s, personId] as [string, ScheduleSlot[], string])
      .sort(([a], [b]) => a.localeCompare(b));
  }, [slots]);

  // Detect split tasks (assigned to multiple people)
  const splitTaskIds = useMemo(() => {
    if (!slots || slots.length === 0) return new Set<string>();
    return detectSplitTaskIds(slots);
  }, [slots]);

  // Group tasks by assigned person for row layout (fallback when no slots)
  const tasksByPerson = useMemo(() => {
    const grouped = new Map<string, ScheduleTask[]>();

    tasks.forEach((task) => {
      if (!task.assignedPerson) return; // skip waiting tasks with no person
      const key = task.assignedPerson.name;
      const existing = grouped.get(key) ?? [];
      grouped.set(key, [...existing, task]);
    });

    // Sort by person name
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [tasks]);

  // Get color class for a task or slot
  function getColor(laborBucket: string, status: string): string {
    if (colorBy === 'status') {
      return statusConfig[status as keyof typeof statusConfig]?.color ?? 'bg-gray-400';
    }
    return laborBucketConfig[laborBucket]?.color ?? 'bg-gray-400';
  }

  // Get label for a task or slot
  function getLabel(laborBucket: string, status: string): string {
    if (colorBy === 'status') {
      return statusConfig[status as keyof typeof statusConfig]?.label ?? status;
    }
    return laborBucketConfig[laborBucket]?.label ?? laborBucket;
  }

  // Legacy color/label helpers for tasks
  function getTaskColor(task: ScheduleTask): string {
    return getColor(task.laborBucket, task.status);
  }

  function getTaskLabel(task: ScheduleTask): string {
    return getLabel(task.laborBucket, task.status);
  }

  const hasData = slotsByPerson ? slotsByPerson.length > 0 : tasks.length > 0;

  if (!hasData) {
    return (
      <div className="flex items-center justify-center py-12 text-foreground-muted">
        No tasks scheduled in this date range
      </div>
    );
  }

  // Determine which row data to render.
  // slotsByPerson includes personId as third element; tasksByPerson does not.
  const rows: [string, (ScheduleSlot | ScheduleTask)[], string | undefined][] =
    slotsByPerson ?? tasksByPerson.map(([name, t]): [string, ScheduleTask[], undefined] => [name, t, undefined]);

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[800px]">
        {/* Timeline header */}
        <div className="flex border-b border-border sticky top-0 bg-background z-10">
          {/* Person column header */}
          <div className="w-40 flex-shrink-0 px-3 py-2 font-medium text-sm text-foreground-muted border-r border-border">
            Resource
          </div>

          {/* Days header */}
          <div className="flex-1 flex">
            {days.map((day) => (
              <div
                key={day.toISOString()}
                className={`
                  flex-1 min-w-[60px] px-1 py-2 text-center text-xs border-r border-border last:border-r-0
                  ${isWeekend(day) ? 'bg-background-muted text-foreground-muted' : 'text-foreground'}
                `}
              >
                <div className="font-medium">{format(day, 'EEE')}</div>
                <div>{format(day, 'MMM d')}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-border">
          {rows.map(([personName, items, personId]) => (
            <div key={personId ?? personName} className="flex min-h-[48px]">
              {/* Person name */}
              <div className="w-40 flex-shrink-0 px-3 py-2 text-sm font-medium text-foreground border-r border-border bg-background-subtle flex items-center">
                <span className="truncate" title={personName}>
                  {personName}
                </span>
              </div>

              {/* Bars area */}
              <div className="flex-1 relative py-1">
                {/* Day column backgrounds */}
                <div className="absolute inset-0 flex pointer-events-none">
                  {days.map((day) => (
                    <div
                      key={day.toISOString()}
                      className={`flex-1 min-w-[60px] border-r border-border/50 last:border-r-0 ${
                        isWeekend(day) ? 'bg-background-muted/50' : ''
                      }`}
                    />
                  ))}
                </div>

                {/* Availability shading - behind task bars */}
                {availability && personId && availability[personId] && (
                  <div className="absolute inset-0 flex pointer-events-none">
                    {days.map((day) => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const dayAvail = availability[personId]?.days[dateStr];
                      if (!dayAvail) return <div key={dateStr} className="flex-1 min-w-[60px] bg-background-muted/50 border-r border-border/50" />;

                      if (dayAvail.type === 'pto' || dayAvail.type === 'sick') {
                        return (
                          <div
                            key={dateStr}
                            className="flex-1 min-w-[60px] bg-red-100/40 border-r border-border/50"
                            title={dayAvail.type === 'pto' ? 'PTO' : 'Sick'}
                          />
                        );
                      }
                      if (dayAvail.type === 'holiday') {
                        return (
                          <div
                            key={dateStr}
                            className="flex-1 min-w-[60px] bg-gray-200/50 border-r border-border/50"
                            title={dayAvail.name ?? 'Holiday'}
                          />
                        );
                      }
                      if (dayAvail.type === 'working' && dayAvail.slots.length > 0) {
                        // Gray base for entire day, white cutouts for working slots
                        return (
                          <div key={dateStr} className="flex-1 min-w-[60px] relative bg-background-muted/30 border-r border-border/50">
                            {dayAvail.slots.map((slot, i) => {
                              const startParts = slot.start.split(':');
                              const endParts = slot.end.split(':');
                              const slotStartHour = parseInt(startParts[0] ?? '0') + parseInt(startParts[1] ?? '0') / 60;
                              const slotEndHour = parseInt(endParts[0] ?? '0') + parseInt(endParts[1] ?? '0') / 60;
                              const clampedStart = Math.max(DAY_START_HOUR, slotStartHour);
                              const clampedEnd = Math.min(DAY_END_HOUR, slotEndHour);
                              if (clampedEnd <= clampedStart) return null;
                              const leftPct = ((clampedStart - DAY_START_HOUR) / HOURS_PER_DAY) * 100;
                              const widthPct = ((clampedEnd - clampedStart) / HOURS_PER_DAY) * 100;
                              return (
                                <div
                                  key={i}
                                  className="absolute top-0 bottom-0 bg-background"
                                  style={{
                                    left: `${leftPct}%`,
                                    width: `${widthPct}%`,
                                  }}
                                />
                              );
                            })}
                          </div>
                        );
                      }
                      // off / no data — solid gray like weekends
                      return <div key={dateStr} className="flex-1 min-w-[60px] bg-background-muted/50 border-r border-border/50" />;
                    })}
                  </div>
                )}

                {/* Render slot bars or task bars */}
                {slotsByPerson
                  ? (items as ScheduleSlot[]).map((slot) => {
                      const position = calculateSlotPosition(slot, gridStart, gridEnd, totalDays);
                      if (!position) return null;
                      const isSplit = splitTaskIds.has(slot.taskId);

                      return (
                        <div
                          key={slot.id}
                          className={`
                            absolute top-1 bottom-1 rounded text-xs text-white font-medium
                            flex items-center px-2 truncate cursor-pointer
                            hover:ring-2 hover:ring-primary hover:ring-offset-1
                            transition-shadow
                            ${getColor(slot.laborBucket, slot.status)}
                            ${isSplit ? 'border border-dashed border-white/60' : ''}
                          `}
                          style={{
                            left: position.left,
                            width: position.width,
                          }}
                          title={`${slot.partNumber} - ${getLabel(slot.laborBucket, slot.status)}`}
                          onMouseMove={(e) => {
                            setHoveredSlot(slot);
                            setTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseLeave={() => setHoveredSlot(null)}
                        >
                          <span className="truncate">
                            {isSplit && (
                              <span className="mr-1 opacity-75" aria-label="Split task">
                                &#x21C4;
                              </span>
                            )}
                            {slot.partNumber}
                          </span>
                        </div>
                      );
                    })
                  : (items as ScheduleTask[]).map((task) => {
                      const position = calculateTaskPosition(task, gridStart, gridEnd, totalDays);
                      if (!position) return null;

                      return (
                        <div
                          key={task.id}
                          className={`
                            absolute top-1 bottom-1 rounded text-xs text-white font-medium
                            flex items-center px-2 truncate cursor-pointer
                            hover:ring-2 hover:ring-primary hover:ring-offset-1
                            transition-shadow
                            ${getTaskColor(task)}
                          `}
                          style={{
                            left: position.left,
                            width: position.width,
                          }}
                          title={`${task.partNumber} - ${getTaskLabel(task)}`}
                          onMouseMove={(e) => {
                            setHoveredTask(task);
                            setTooltipPos({ x: e.clientX, y: e.clientY });
                          }}
                          onMouseLeave={() => setHoveredTask(null)}
                        >
                          <span className="truncate">
                            {task.partNumber}
                          </span>
                        </div>
                      );
                    })}
              </div>
            </div>
          ))}
        </div>

        {/* Hover tooltip for slots */}
        {hoveredSlot && (
          <div
            className="fixed bg-background border border-border rounded-lg shadow-lg p-4 max-w-sm z-50 pointer-events-none"
            style={{
              left: `${Math.min(tooltipPos.x + 12, window.innerWidth - 320)}px`,
              top: `${Math.min(tooltipPos.y + 12, window.innerHeight - 200)}px`,
            }}
          >
            <div className="font-mono text-sm font-medium text-foreground">
              {hoveredSlot.partNumber}
            </div>
            <div className="text-sm text-foreground-muted mt-1">
              {hoveredSlot.gpSalesOrderNumber}
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <div>
                <span className="text-foreground-muted">Status:</span>{' '}
                <span className="font-medium">
                  {statusConfig[hoveredSlot.status as keyof typeof statusConfig]?.label ?? hoveredSlot.status}
                </span>
              </div>
              <div>
                <span className="text-foreground-muted">Type:</span>{' '}
                <span className="font-medium">
                  {laborBucketConfig[hoveredSlot.laborBucket]?.label ?? hoveredSlot.laborBucket}
                </span>
              </div>
            </div>
            <div className="text-xs mt-1">
              <span className="text-foreground-muted">Assigned:</span>{' '}
              <span className="font-medium">{hoveredSlot.personName}</span>
            </div>
            <div className="text-xs mt-1">
              <span className="text-foreground-muted">Duration:</span>{' '}
              <span className="font-medium">{formatDuration(hoveredSlot.durationMinutes)}</span>
            </div>
            <div className="text-xs mt-1 font-mono text-foreground-muted">
              {format(parseISO(hoveredSlot.startTime), 'MMM d, h:mm a')} -{' '}
              {format(parseISO(hoveredSlot.endTime), 'MMM d, h:mm a')}
            </div>
            {splitTaskIds.has(hoveredSlot.taskId) && (
              <div className="text-xs mt-2 text-amber-600 font-medium">
                Split task (shared across multiple people)
              </div>
            )}
          </div>
        )}

        {/* Hover tooltip for tasks (fallback mode) */}
        {hoveredTask && !hoveredSlot && (
          <div
            className="fixed bg-background border border-border rounded-lg shadow-lg p-4 max-w-sm z-50 pointer-events-none"
            style={{
              left: `${Math.min(tooltipPos.x + 12, window.innerWidth - 320)}px`,
              top: `${Math.min(tooltipPos.y + 12, window.innerHeight - 200)}px`,
            }}
          >
            <div className="font-mono text-sm font-medium text-foreground">
              {hoveredTask.partNumber}
            </div>
            <div className="text-sm text-foreground-muted mt-1">
              {hoveredTask.gpSalesOrderNumber}
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <div>
                <span className="text-foreground-muted">Status:</span>{' '}
                <span className="font-medium">{statusConfig[hoveredTask.status]?.label}</span>
              </div>
              <div>
                <span className="text-foreground-muted">Type:</span>{' '}
                <span className="font-medium">{laborBucketConfig[hoveredTask.laborBucket]?.label}</span>
              </div>
            </div>
            {hoveredTask.assignedPerson && (
              <div className="text-xs mt-1">
                <span className="text-foreground-muted">Assigned:</span>{' '}
                <span className="font-medium">{hoveredTask.assignedPerson.name}</span>
              </div>
            )}
            {hoveredTask.scheduledStart && (
              <div className="text-xs mt-1 font-mono text-foreground-muted">
                {format(parseISO(hoveredTask.scheduledStart), 'MMM d, h:mm a')} -{' '}
                {hoveredTask.scheduledEnd && format(parseISO(hoveredTask.scheduledEnd), 'MMM d, h:mm a')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Legend component for the Gantt chart -- shows only types present in the data
interface GanttLegendProps {
  colorBy: 'laborBucket' | 'status';
  tasks?: ScheduleTask[];
  slots?: ScheduleSlot[];
}

export function GanttLegend({ colorBy, tasks, slots }: GanttLegendProps) {
  const items = useMemo(() => {
    const allItems = colorBy === 'status'
      ? Object.entries(statusConfig)
      : Object.entries(laborBucketConfig);

    // Collect present keys from either slots or tasks
    const presentKeys = new Set<string>();

    if (slots && slots.length > 0) {
      slots.forEach((s) => presentKeys.add(colorBy === 'status' ? s.status : s.laborBucket));
    } else if (tasks && tasks.length > 0) {
      tasks.forEach((t) => presentKeys.add(colorBy === 'status' ? t.status : t.laborBucket));
    }

    if (presentKeys.size > 0) {
      return allItems.filter(([key]) => presentKeys.has(key));
    }

    return allItems;
  }, [colorBy, tasks, slots]);

  return (
    <div className="flex flex-wrap gap-3 text-xs">
      {items.map(([key, config]) => (
        <div key={key} className="flex items-center gap-1.5">
          <div className={`w-3 h-3 rounded ${config.color}`} />
          <span className="text-foreground-muted">{config.label}</span>
        </div>
      ))}
    </div>
  );
}
